import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $userStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import type { ProfileResponse } from '@shared/api';
import { UserSidebarItems } from './userSidebarItems';
import { ChangePasswordModal, DeleteUserModal, EditDetailsModal, LockUserModal } from '../modals';
import { Button, Spinner } from '@shared/widgets';

export interface UserSidebarProps {
  className?: string;
  user: ProfileResponse;
}

export const UserSidebar = (props: UserSidebarProps) => {
  const userStore = useUnit($userStore.store);

  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [showLockModal, setShowLockModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { t } = useTranslation('user');

  return (
    <aside className={cn('h-fit w-full max-w-80 shrink-0', 'max-xl:max-w-none', props.className)}>
      <Spinner show={userStore.actionLoading} position />
      {props.user.locked && (
        <Button fullWidth color='primary' onClick={() => $userStore.events.unlockUser(props.user.id)}>
          {t('buttons.unlock', { ns: 'common' })}
        </Button>
      )}
      <div
        className={cn(
          props.user.locked && [
            'relative flex items-center justify-center p-[15px]',
            'before:absolute before:left-0 before:z-[40] before:h-full before:w-full before:rounded-sm before:bg-white before:opacity-40',
          ],
        )}
      >
        <div
          className={cn(
            'bg-white p-6 dark:bg-soft-black',
            PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
            props.className,
          )}
        >
          <UserSidebarItems
            user={props.user}
            onShowDetailsModal={() => setShowDetailsModal(true)}
            onShowLockModal={() => setShowLockModal(true)}
            onShowPasswordModal={() => setShowPasswordModal(true)}
            onShowDeleteModal={() => setShowDeleteModal(true)}
          />
        </div>
      </div>
      <EditDetailsModal show={showDetailsModal} onClose={() => setShowDetailsModal(false)} user={props.user} />
      <ChangePasswordModal show={showPasswordModal} onClose={() => setShowPasswordModal(false)} userId={props.user.id} />
      <LockUserModal isOpen={showLockModal} onClose={() => setShowLockModal(false)} user={props.user} />
      <DeleteUserModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} userId={props.user.id} />
    </aside>
  );
};
